import React, { JSX, ReactNode, useEffect, useState } from 'react'
import { FaSort } from 'react-icons/fa'
import { TiArrowSortedDown, TiArrowSortedUp } from 'react-icons/ti'

import TopScrollTable from './TopScrollTable.tsx'

export interface Column<T> {
  header: string
  headerSecondLine?: string
  accessor: keyof T | string
  render?: (data: T) => JSX.Element
  className?: string
  classNameTd?: string
  classNameHeaderFirstLine?: string
  classNameHeaderSecondLine?: string
  conditionalClassNameCell?: (data: T) => string // New property for conditional cell styling
  sorted?: boolean
  sortDirection?: 'asc' | 'desc'
  onHover?: (row: T) => { label?: string; value?: string }[] | null
  onClick?: (row: T) => JSX.Element | null
}

export interface Action<T> {
  label: string
  icon?: ReactNode
  onClick: (row: T, event: React.MouseEvent<HTMLButtonElement>) => void
  onHover?: (row: T) => { [key: string]: string }
  className?: string
  visible?: (row: T) => boolean
}

interface TableProps<T> {
  columns: Column<T>[]
  data: T[]
  actions?: Action<T>[]
  tableClassName?: string
  rowClassName?: (row: T) => string // New prop to apply dynamic row classes
}

const Table = <T,>({
  columns,
  data,
  actions,
  tableClassName = 'table-fixed w-full min-w-[600px]',
  rowClassName
}: TableProps<T>) => {
  const [sortedData, setSortedData] = useState<T[]>(data)
  const [hoveredRow, setHoveredRow] = useState<number | null>(null)
  const [expandedCol, setExpandedCol] = useState<
    { label?: string; value?: string }[] | null
  >(null)
  const [expanded, setExpanded] = useState<{ [key: string]: string } | null>(
    null
  )
  const [popupData, setPopupData] = useState<{
    content: React.ReactNode | null
    position: { x: number; y: number } | null
  }>({
    content: null,
    position: null
  })

  const indexOfFirstColumnToSort = columns.findIndex((col) => col.sorted)
  const [directionData, setDirectionData] = useState<{
    col: number
    direction: 'asc' | 'desc'
  }>({
    col: indexOfFirstColumnToSort ?? 0,
    direction: columns[indexOfFirstColumnToSort]?.sortDirection ?? 'desc'
  })

  useEffect(() => {
    if (indexOfFirstColumnToSort === -1) {
      setSortedData(data)
      return
    }
    const numberOfColumnsSorted = directionData.col

    const dataAfterSorted =
      directionData?.direction === 'asc'
        ? [...data].sort((a, b) =>
            a[columns[numberOfColumnsSorted].accessor as keyof T] >
            b[columns[numberOfColumnsSorted].accessor as keyof T]
              ? 1
              : -1
          )
        : [...data].sort((a, b) =>
            a[columns[numberOfColumnsSorted].accessor as keyof T] <
            b[columns[numberOfColumnsSorted].accessor as keyof T]
              ? 1
              : -1
          )
    setSortedData(dataAfterSorted)
  }, [directionData, data, columns])

  const handleSortClick = (i: number, direction: 'asc' | 'desc') => {
    setDirectionData({ col: i, direction })
  }

  const handleCellClick = (
    event: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    col: any,
    row: any
  ) => {
    // Get the bounding rectangle of the cell and the table
    const cellRect = event.currentTarget.getBoundingClientRect()
    const tableRect = event.currentTarget
      .closest('table')
      ?.getBoundingClientRect()

    if (tableRect) {
      setPopupData({
        content: col.onClick ? col.onClick(row) : 'Default Popup Content',
        position: {
          x: cellRect.right - tableRect.left - cellRect.width - 100,
          y: cellRect.top - tableRect.top
        }
      })
    }
  }

  return (
    <div className="max-w-full overflow-x-auto relative">
      <TopScrollTable>
        <table className={tableClassName}>
          <thead>
            <tr className="dark:bg-meta-4">
              {columns.map((col, i) => (
                <th
                  key={`header-${i}`}
                  className={`px-4 font-medium text-left text-black dark:text-white ${col.className || ''}`}
                >
                  <div className="flex flex-col w-full">
                    {!col.sorted && (
                      <>
                        <div className={col.classNameHeaderFirstLine}>
                          {col.header}
                        </div>
                        {col.headerSecondLine && (
                          <div className={col.classNameHeaderSecondLine}>
                            {col.headerSecondLine}
                          </div>
                        )}
                      </>
                    )}
                    {col.sorted && (
                      <div
                        onClick={() =>
                          handleSortClick(
                            i,
                            directionData.direction === 'asc' ? 'desc' : 'asc'
                          )
                        }
                        className="flex flex-wrap w-full justify-around items-center py-4 gap-2 cursor-pointer"
                      >
                        <div className="flex flex-1 flex-row w-full justify-start items-center gap-x-4 ">
                          <div className="flex flex-col">
                            <div className={col.classNameHeaderFirstLine}>
                              {col.header}
                            </div>
                            {col.headerSecondLine && (
                              <div className={col.classNameHeaderSecondLine}>
                                {col.headerSecondLine}
                              </div>
                            )}
                          </div>
                          {directionData.col !== i && <FaSort />}
                          {directionData.col === i &&
                            directionData.direction === 'asc' && (
                              <TiArrowSortedUp />
                            )}
                          {directionData.col === i &&
                            directionData.direction === 'desc' && (
                              <TiArrowSortedDown />
                            )}
                        </div>

                        <div className="flex justify-center items-center"></div>
                      </div>
                    )}
                  </div>
                </th>
              ))}
              {actions && (
                <th
                  key={'action-header'}
                  className="py-4 px-4 text-center font-medium text-black dark:text-white w-[150px] xl:w-auto"
                >
                  Więcej
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => (
              <React.Fragment key={`row-block-${rowIndex}`}>
                <tr
                  key={rowIndex}
                  className={`odd:bg-gray-50 even:bg-white dark:odd:bg-gray-800 dark:even:bg-gray-900 
    ${rowClassName ? rowClassName(row) : ''}`}
                >
                  {columns.map((col, colIndex) => (
                    <td
                      key={`row-${rowIndex}-col-${colIndex}`}
                      className={`py-3 px-4 
      ${rowIndex === sortedData.length - 1 ? '' : 'border-b dark:border-strokedark'} 
      ${col.classNameTd || ''} 
      ${col.conditionalClassNameCell ? col.conditionalClassNameCell(row) : ''} 
     `}
                      onMouseLeave={() => {
                        setHoveredRow(null)
                        setExpanded(null)
                      }}
                      onMouseOver={() => {
                        if (col.onHover !== undefined) {
                          setHoveredRow(rowIndex)
                          const expandedFields = col.onHover(row)
                          setExpandedCol(expandedFields)
                        }
                      }}
                      onClick={(event) => {
                        if (col.onClick !== undefined) {
                          handleCellClick(event, col, row)
                        }
                      }}
                    >
                      {col.render
                        ? col.render(row)
                        : (row[col.accessor as keyof T] as React.ReactNode)}
                    </td>
                  ))}
                  {actions && (
                    <td
                      className={`flex-col justify-center py-3 px-4 ${rowIndex === sortedData.length - 1 ? '' : 'border-b dark:border-strokedark'}`}
                    >
                      {/* Render all provided actions */}
                      {actions.map((action, actionIndex) => {
                        const isVisible = action.visible
                          ? action.visible(row)
                          : true
                        return isVisible ? (
                          <button
                            key={`row-${rowIndex}-action-${actionIndex}`}
                            onClick={(event) => action.onClick(row, event)}
                            onMouseLeave={() => {
                              setHoveredRow(null)
                              setExpanded(null)
                            }}
                            onMouseOver={() => {
                              if (action.onHover !== undefined) {
                                setHoveredRow(rowIndex)
                                const expandedFields: {
                                  [key: string]: string
                                } | null = action.onHover(row)
                                setExpanded(expandedFields)
                              }
                            }}
                            className={`flex m-auto items-center space-x-1 ${action.className}`}
                          >
                            {action.icon}
                            <span>{action.label}</span>
                          </button>
                        ) : null
                      })}
                    </td>
                  )}
                </tr>
                {hoveredRow === rowIndex &&
                  expandedCol !== null &&
                  Object.keys(expandedCol).length > 0 && (
                    <>
                      <tr key={`expandedLabel-${rowIndex}`}>
                        {Object.values(expandedCol).map(
                          (row: { label?: string; value?: string }, index) => (
                            <td
                              key={`expandedLabel-${rowIndex}-value-${index}`}
                              className={`bg-gray-100 border-b-1 py-3 px-4 dark:border-strokedark ${(columns.length - 1 > index && columns[index].classNameTd) || ''}`}
                            >
                              {row.label !== 'hidden' ? row.label : ''}
                            </td>
                          )
                        )}
                      </tr>
                      <tr key={`expanded-${rowIndex}`}>
                        {Object.values(expandedCol).map(
                          (row: { label?: string; value?: string }, index) => (
                            <td
                              key={`expanded-${rowIndex}-value-${index}`}
                              className={`border-b-2 py-3 px-4 dark:border-strokedark ${(columns.length - 1 > index && columns[index].classNameTd) || ''}`}
                            >
                              {row.value !== 'hidden' ? row.value : ''}
                            </td>
                          )
                        )}
                      </tr>
                    </>
                  )}
                {hoveredRow === rowIndex &&
                  expanded !== null &&
                  Object.keys(expanded).length > 0 && (
                    <tr key={`expanded-${rowIndex}`}>
                      {Object.values(expanded).map((value, index) => (
                        <td
                          key={`expanded-${rowIndex}-value-${index}`}
                          className={`bg-gray-100 border-b-6 py-5 px-4 dark:border-strokedark ${(columns.length - 1 > index && columns[index].classNameTd) || ''}`}
                        >
                          {value !== 'hidden' ? value : ''}
                        </td>
                      ))}
                    </tr>
                  )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </TopScrollTable>
      {/* Popup component */}
      {popupData.content && popupData.position && (
        <div
          className="absolute z-10 bg-white border shadow-lg rounded p-4 max-w-xs xsm:w-full flex flex-1 dark:bg-gray-800 dark:border-strokedark"
          style={{
            top: `${popupData.position.y}px`,
            left: `${popupData.position.x}px`
          }}
        >
          <button
            className="absolute top-1 right-1 text-gray-500 hover:text-gray-800"
            onClick={
              () => setPopupData({ content: null, position: null }) // Close popup
            }
          >
            ✖
          </button>
          {popupData.content}
        </div>
      )}
    </div>
  )
}

export default Table
